import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    violenceRisks: [],
    currentViolenceRisk: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Violence Risk Assessments
export const fetchViolenceRisks = createAsyncThunk(
    'violenceRisks/fetchViolenceRisks',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/violence-risks');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch violence risk assessments'
            );
        }
    }
);

// Fetch a single Violence Risk Assessment
export const fetchViolenceRisk = createAsyncThunk(
    'violenceRisks/fetchViolenceRisk',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/violence-risks/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch violence risk assessment'
            );
        }
    }
);

// Create a new Violence Risk Assessment
export const createViolenceRisk = createAsyncThunk(
    'violenceRisks/createViolenceRisk',
    async (violenceRiskData, { rejectWithValue }) => {
        try {
            const response = await api.post('/violence-risks', violenceRiskData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create violence risk assessment'
            );
        }
    }
);

// Update an existing Violence Risk Assessment
export const updateViolenceRisk = createAsyncThunk(
    'violenceRisks/updateViolenceRisk',
    async ({ case_id, violenceRiskData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/violence-risks/case/${case_id}`, violenceRiskData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update violence risk assessment'
            );
        }
    }
);

// Delete a Violence Risk Assessment
export const deleteViolenceRisk = createAsyncThunk(
    'violenceRisks/deleteViolenceRisk',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/violence-risks/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete violence risk assessment'
            );
        }
    }
);

// ==================== Slice ====================
const violenceRisksSlice = createSlice({
    name: 'violenceRisks',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentViolenceRisk: (state, action) => {
            state.currentViolenceRisk = action.payload;
        },
        clearCurrentViolenceRisk: (state) => {
            state.currentViolenceRisk = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchViolenceRisks.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchViolenceRisks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.violenceRisks = action.payload;
            })
            .addCase(fetchViolenceRisks.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchViolenceRisk.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchViolenceRisk.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentViolenceRisk = action.payload;
            })
            .addCase(fetchViolenceRisk.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createViolenceRisk.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createViolenceRisk.fulfilled, (state, action) => {
                state.isLoading = false;
                state.violenceRisks.unshift(action.payload);
            })
            .addCase(createViolenceRisk.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateViolenceRisk.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateViolenceRisk.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.violenceRisks.findIndex(
                    (d) => d.id === updated.id
                );
                if (index !== -1) {
                    state.violenceRisks[index] = updated;
                }

                if (state.currentViolenceRisk?.id === updated.id) {
                    state.currentViolenceRisk = updated;
                }
            })
            .addCase(updateViolenceRisk.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteViolenceRisk.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteViolenceRisk.fulfilled, (state, action) => {
                state.isLoading = false;
                state.violenceRisks = state.violenceRisks.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentViolenceRisk?.id === action.payload) {
                    state.currentViolenceRisk = null;
                }
            })
            .addCase(deleteViolenceRisk.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentViolenceRisk,
    clearCurrentViolenceRisk,
} = violenceRisksSlice.actions;

export default violenceRisksSlice.reducer;
